import clsx from 'clsx';
import { Label } from '../../../../../components/Label';
import React, { ChangeEvent, useState } from 'react';
import { useForm } from '../../../../../hooks/useForm';
import { Input } from '../../../../../components/Input';
import { SettingItemActions } from '../../SettingItemActions';
import { Select, SelectItem } from '../../../../../components/Select';
import { SettingTag } from '../../SettingTag';
import { CreateSubscriberGroup } from '../../../../../hooks/SubscriberGroups/useSubscriberGroups';

interface Props {
  index: number;
  subscriberGroup: CreateSubscriberGroup;
  onRemove?(index: number): void;
  onSave?(item: CreateSubscriberGroup, index: number): void;
}

interface Content extends Props {
  editing: boolean;
}

interface Form extends Content {
  values: CreateSubscriberGroup;
  onChange(event: ChangeEvent<HTMLInputElement>): void;
  updateField(field: keyof CreateOperatorPackage, value: any): void;
}

export const Package = (props: Props) => {
  const [editing, setEditing] = useState(false);
  const { index, onRemove = null, onSave = null, subscriberGroup } = props;
  const { values, onChange, updateField } = useForm<CreateSubscriberGroup>(
    subscriberGroup,
    () => null
  );

  return (
    <div
      className={clsx(
        'w-full inline-flex items-center justify-between gap-4 h-14'
      )}
    >
      <EditPackage
        editing={editing}
        onChange={onChange}
        updateField={updateField}
        values={values}
        {...props}
      />
      <PackageContent editing={editing} {...props} />
      {onRemove && onSave ? (
        <SettingItemActions
          canSave={
            values.type !== null &&
            (values.name ?? '').trim() !== '' &&
            values.description.trim() !== ''
          }
          editing={editing}
          onEdit={() => setEditing(true)}
          onRemove={() => onRemove(index)}
          onSave={() => {
            onSave(values, index);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : null}
    </div>
  );
};

const EditPackage = ({ editing, values, onChange, updateField }: Form) => {
  const defaultProps: any = {
    variant: 'contained',
    onChange: onChange,
    required: true,
  };

  if (editing === false) return null;
  return (
    <fieldset className="inline-flex items-center gap-4">
      <Select
        label="Type"
        value={values.type || ''}
        onChange={(event) => updateField('type', event.target.value)}
        required
        variant="contained"
      >
        <SelectItem value="base">Base</SelectItem>
        <SelectItem value="alaCarte">À La Carte</SelectItem>
        <SelectItem value="addons">Add-On</SelectItem>
      </Select>
      <Input
        name="description"
        label="Name"
        value={values.description}
        {...defaultProps}
      />
      <Input
        name="name"
        label="Subscriber Group"
        value={values.name}
        {...defaultProps}
      />
    </fieldset>
  );
};

const PackageContent = ({ editing, subscriberGroup }: Content) => {
  if (editing) return null;

  const formatPackageType = (type?: PackageType) => {
    switch (type) {
      case 'base':
        return 'Base';
      case 'alaCarte':
        return 'À La Carte';
      case 'addons':
        return 'Add-On';
      default:
        return 'N/A';
    }
  };

  const getTagColor = (type?: PackageType): 'amber' | 'violet' | 'teal' => {
    switch (type) {
      case 'base':
        return 'amber';
      case 'alaCarte':
        return 'teal';
      case 'addons':
        return 'violet';
      default:
        return 'amber';
    }
  };

  return (
    <>
      <SettingTag
        name={formatPackageType(subscriberGroup.type || undefined)}
        color={getTagColor(subscriberGroup.type || undefined)}
      />
      <Label text="Package Name:" align="left">
        {subscriberGroup.description}
      </Label>
      <Label text="Subscriber Group:" align="left">
        {subscriberGroup.name}
      </Label>
    </>
  );
};
